import React from 'react';
import { useLibkeyResult } from '../components/LibkeyProvider';
import { useOptionalFilesForReference } from '../components/StudyReferenceFilesProvider';
import * as styles from './LibkeyLink.module.scss';
import {
  LibkeyAccessType,
  LibkeyArticleResult,
  LibkeyReferenceId,
} from 'query/study/LibKey/Articles';
import { Link, Text } from 'components/core';

type LibkeyLinkProps = {
  referenceId: LibkeyReferenceId;
  /** Extra class for the link wrapper, e.g. to add spacing in a given list. */
  className?: string;
  /**
   * Whether the full text has already been uploaded for this reference, used to
   * hide the link. Lists wrapped in a StudyReferenceFilesProvider (screening,
   * full-text review, included, …) can leave this unset — the link derives it
   * from that provider. Lists that track uploads elsewhere (the DE2 extraction
   * list) pass it explicitly.
   */
  hasUploadedFullText?: boolean;
};

// Link text per access type, without the library suffix. Direct-access types
// (full_text_file, content_location) promise the full text; hand-off types
// (document_delivery, link_resolver) only promise a check. The three alert
// states (:article_retracted, :expression_of_concern, :problematic_journal),
// :unknown and :error are `null` here as they are not to be handled by the
// link.
const LINK_TEXT: Record<LibkeyAccessType, string | null> = {
  full_text_file: 'Access full text',
  content_location: 'Access full text',
  document_delivery: 'Check full text availability',
  link_resolver: 'Check full text availability',
  article_retracted: null,
  expression_of_concern: null,
  problematic_journal: null,
  unknown: null,
  error: null,
};

const renderLink = (
  result: LibkeyArticleResult,
  openAccessLibrary: boolean
): JSX.Element | null => {
  const action = LINK_TEXT[result.access_type];

  if (!action || !result.url) {
    return null;
  }

  // The reviewer's own library gets "your library"; the Open Access (Open
  // Alex) fallback names itself so reviewers aren't told they have a library
  // subscription they don't.
  const text = `${action} via ${
    openAccessLibrary ? 'Open Access library' : 'your library'
  }`;

  return (
    <span>
      <Link
        to={result.url}
        external
        variant="primary"
        data-pendo-key="libkey-link"
      >
        {text}
      </Link>{' '}
      <Text variant="weak" component="span">
        and upload PDF below
      </Text>
    </span>
  );
};

/**
 * Renders the LibKey access link for a single reference, or nothing when there
 * is no link to show. We render nothing until there is an actual link: a
 * disabled list, a result not fetched yet, no LibKey match, or a match with no
 * renderable link (:unknown, :error, missing url) all render nothing at all.
 *
 * We also hide the link once a PDF has been uploaded for the reference: the
 * point of the link is to help the user find and upload the full text, so once
 * they have we stop nudging them. A full-text *link* (reference.document_url)
 * is not an uploaded file — the user still hasn't completed the task — so those
 * references keep showing the link.
 */
export const LibkeyLink = ({
  referenceId,
  className,
  hasUploadedFullText,
}: LibkeyLinkProps) => {
  const { isEnabled, openAccessLibrary, result } = useLibkeyResult(referenceId);
  // Number() because the files provider keys by numeric reference id; the
  // study lists always pass a numeric reference.id (a non-numeric id would
  // simply yield no files, leaving the link visible).
  const files = useOptionalFilesForReference(Number(referenceId));

  // Prefer the explicit prop when a caller supplies it (the DE2 list, which
  // tracks uploads outside the files provider); otherwise derive it from the
  // provider's files.
  const hasUploadedPdf =
    hasUploadedFullText ?? files.some((file) => file.uploadComplete);

  const link =
    isEnabled && result ? renderLink(result, openAccessLibrary) : null;
  if (!link || hasUploadedPdf) {
    return null;
  }

  return (
    <div
      className={[styles.slot, className].filter(Boolean).join(' ')}
      data-testid="libkey-slot"
    >
      {link}
    </div>
  );
};
